/**
 * Market Data Contracts
 * Types for market data management admin module (inventory, downloads, jobs)
 */

// ============================================================
// CANDLE INTERVALS
// ============================================================

export const CANDLE_INTERVALS = [
  '1m',
  '3m',
  '5m',
  '15m',
  '30m',
  '1h',
  '2h',
  '4h',
  '6h',
  '8h',
  '12h',
  '1d',
  '3d',
  '1w',
  '1M',
] as const

export type CandleInterval = (typeof CANDLE_INTERVALS)[number]

/**
 * Duration of one candle bucket, in seconds, keyed by interval.
 *
 * Coverage is "how many of the expected buckets do we actually have?". Measuring
 * an intraday interval (1h, 5m, …) against a calendar-DAY denominator produces
 * nonsense (a full 1h day has 24 candles → 2400% "coverage" and undetectable
 * gaps). These durations give the correct denominator: expected buckets over a
 * span = floor(span_seconds / bucket_seconds) + 1.
 *
 * '1M' (monthly) is approximated to 30 days — calendar months vary (28–31 days),
 * but monthly candles are rare and their coverage is a heuristic gate, never a
 * tax-filing input, so the small drift is acceptable.
 */
export const CANDLE_INTERVAL_SECONDS: Record<CandleInterval, number> = {
  '1m': 60,
  '3m': 180,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '6h': 21600,
  '8h': 28800,
  '12h': 43200,
  '1d': 86400,
  '3d': 259200,
  '1w': 604800,
  '1M': 2592000,
}

/**
 * Seconds in one bucket of `interval`. Unknown strings fall back to a daily
 * bucket (86400s) so coverage math degrades to calendar-day behavior instead of
 * throwing on an unexpected value.
 */
export function candleIntervalToSeconds(interval: string): number {
  return CANDLE_INTERVAL_SECONDS[interval as CandleInterval] ?? 86400
}

// ============================================================
// INVENTORY
// ============================================================

export interface MarketDataInventoryStats {
  totalAssetsWithData: number
  totalCandleRecords: number
  totalFxRecords: number
  dateRangeStart: string | null
  dateRangeEnd: string | null
  sourcesCount: number
  /**
   * The DISTINCT `source` values actually present in `bat.candles`, sorted.
   * The inventory filter used to offer a hardcoded Binance/CoinGecko/ECB list
   * that matched neither reality (`binance`, `synthetic`) nor this table — so
   * two options returned nothing and the `synthetic` rows could not be
   * filtered at all. Driving the dropdown off this keeps the two in step.
   */
  sources: string[]
  assetsWithGaps: number
  intervalBreakdown: Record<string, number>
}

export interface MarketDataGap {
  from: string
  to: string
  missingDays: number
}

export interface MarketDataAssetCoverage {
  assetId: string
  assetName: string
  assetSymbol: string
  assetType: string
  assetCategory: string | null
  source: string
  interval: CandleInterval
  firstDate: string
  lastDate: string
  recordCount: number
  expectedDays: number
  coveragePercent: number
  gaps: MarketDataGap[]
  totalMissingDays: number
}

/**
 * Columns the inventory table can be ordered by ON THE SERVER. The value IS the
 * table column id, so the header the admin clicks and the key the endpoint reads
 * are the same word. `sources` is absent on purpose: that cell is a badge list,
 * not a value, and its column declares `enableSorting: false`.
 */
export const INVENTORY_COVERAGE_SORT_KEYS = ['asset', 'type', 'timeframes', 'health'] as const
export type InventoryCoverageSortKey = (typeof INVENTORY_COVERAGE_SORT_KEYS)[number]

export interface InventoryCoverageQuery {
  search?: string
  source?: string
  type?: string
  gapsOnly?: boolean
  interval?: CandleInterval
  /** Absent = the endpoint's own default order (by symbol). */
  sortBy?: InventoryCoverageSortKey
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

// ============================================================
// FILL GAPS
// ============================================================

export interface FillGapsRequest {
  assetId: string
  assetSymbol: string
  exchangeSlug: string
  gaps: MarketDataGap[]
  interval: CandleInterval
  baseCurrency?: string
}

export interface FillAllGapsRequest {
  exchangeSlug: string
  interval: CandleInterval
  baseCurrency?: string
  maxJobs?: number
}

// ============================================================
// DOWNLOAD
// ============================================================

export interface MarketDataDownloadRequest {
  exchangeSlug: string
  assets: string[]
  dateRange: { startDate: string; endDate: string }
  interval: CandleInterval
  baseCurrency?: string
  onlyMissing?: boolean
}

export interface DownloadEstimationRequest {
  exchangeSlug: string
  assets: string[]
  dateRange: { startDate: string; endDate: string }
  interval: CandleInterval
  /**
   * Mirrors `MarketDataDownloadRequest.onlyMissing` (default true). The estimate
   * exists to describe the button under it, and that button behaves differently
   * in the two modes: with `false` the whole range is re-requested, existing
   * candles included, so an estimate blind to this field answered "up to date ·
   * 0 jobs" for a press that re-downloaded the range.
   */
  onlyMissing?: boolean
}

export interface DownloadEstimationItem {
  assetSymbol: string
  existingRecords: number
  requestedDays: number
  toDownload: number
  /** `refresh_all`: nothing is missing, but the mode re-requests the range anyway. */
  status: 'up_to_date' | 'has_gaps' | 'no_data' | 'refresh_all'
  gaps: MarketDataGap[]
}

export interface DownloadEstimationResponse {
  items: DownloadEstimationItem[]
  totalExisting: number
  totalToDownload: number
  jobsToCreate: number
  estimatedTimeSeconds: number
}

// ============================================================
// ECB FX RATES
// ============================================================

export const ECB_SUPPORTED_CURRENCIES = [
  'USD',
  'JPY',
  'BGN',
  'CZK',
  'DKK',
  'GBP',
  'HUF',
  'PLN',
  'RON',
  'SEK',
  'CHF',
  'ISK',
  'NOK',
  'TRY',
  'AUD',
  'BRL',
  'CAD',
  'CNY',
  'HKD',
  'IDR',
  'ILS',
  'INR',
  'KRW',
  'MXN',
  'MYR',
  'NZD',
  'PHP',
  'SGD',
  'THB',
  'ZAR',
] as const

export type EcbCurrency = (typeof ECB_SUPPORTED_CURRENCIES)[number]

export interface EcbFxSyncRequest {
  currencies: EcbCurrency[]
  dateRange: { startDate: string; endDate: string }
}

export interface EcbFxEstimation {
  currencies: string[]
  existingRecords: number
  estimatedNewRecords: number
  dateRange: { startDate: string; endDate: string }
}

// ============================================================
// EXCHANGE PAIRS — en `./exchange-pairs.js` (index.ts rozaba las 500 líneas)
// ============================================================

export type {
  ExchangePairInfo,
  ExchangeQuoteAssetInfo,
  ExchangePairDiscoveryResult,
} from './exchange-pairs.js'

// ============================================================
// DOWNLOAD SOURCES (#2220)
// ============================================================

/**
 * #2220: por qué una fuente de precios NO se puede elegir en la pestaña
 * Descarga. Existe porque «no hay ese activo aquí» y «esta fuente nunca se
 * cableó» se decían igual («Sin resultados»), y eso hacía el defecto
 * invisible: el operador concluía que el activo no existe en ninguna parte.
 *   - 'endpoint-disabled':  hay fila en bat.klines_endpoints pero enabled=false.
 *   - 'no-asset-catalogue': la fila está activa (se puede DESCARGAR de ella),
 *                           pero el panel no sabe listar sus activos, así que
 *                           el operador no podría elegir ninguno.
 *   - 'missing-credential': la fila declara su credencial en `auth.envVar` y esa
 *                           variable de entorno no tiene valor, así que TODA
 *                           descarga fallaría con 401 (#2231: CryptoCompare se
 *                           ofrecía como elegible sin CRYPTOCOMPARE_API_KEY).
 */
export type MarketDataSourceUnavailableReason =
  | 'endpoint-disabled'
  | 'no-asset-catalogue'
  | 'missing-credential'

/**
 * #2220: una fuente de descarga tal y como EXISTE en la base, no como la
 * recuerda una lista escrita a mano en el frontend. `selectable` se deriva del
 * estado real (¿hay fila activa en bat.klines_endpoints? ¿sabe el panel listar
 * sus activos?), de modo que el día que #2004 siembre las filas de CoinGecko
 * la opción aparece sola, sin tocar la UI.
 */
export interface MarketDataDownloadSource {
  /** Slug del exchange/fuente, tal cual en bat.klines_endpoints.exchange_slug. */
  slug: string
  /** Nombre para mostrar (bat.price_source_configs.display_name) o el slug. */
  displayName: string
  /** true sólo si el operador puede elegirla Y llegar a un activo con ella. */
  selectable: boolean
  /** Motivo cuando `selectable` es false; null cuando sí lo es. */
  unavailableReason: MarketDataSourceUnavailableReason | null
  /**
   * #2231: NOMBRE de la variable de entorno que falta cuando
   * `unavailableReason === 'missing-credential'`; null en cualquier otro caso.
   * Viaja el nombre para que el operador sepa QUÉ poner y dónde; su VALOR no
   * sale nunca de la máquina.
   */
  missingCredentialEnvVar: string | null
}

// ============================================================
// PARQUET IMPORT/EXPORT
// ============================================================

export interface ParquetExportRequest {
  table: 'candles' | 'fx_rates'
  filters?: {
    assetIds?: string[]
    dateRange?: { startDate: string; endDate: string }
    sources?: string[]
    interval?: CandleInterval
  }
}

export interface ParquetImportResult {
  recordsImported: number
  recordsSkipped: number
  recordsUpdated: number
  errors: string[]
}

// ============================================================
// JOBS
// ============================================================

export interface MarketDataJobResponse {
  id: string
  jobType: string
  status: string
  progress: number
  progressMessage?: string
  inputData: Record<string, unknown>
  outputData?: Record<string, unknown>
  errorData?: Record<string, unknown>
  metadata?: Record<string, unknown>
  createdAt: string
  startedAt?: string
  completedAt?: string
  processingTimeMs?: number
  currentRetry: number
  maxRetries: number
  checkpoint?: { stage: string; cursor: unknown; lastUpdated: string | null } | null
}

export interface MarketDataJobsQuery {
  status?: string
  exchange?: string
  action?: string
  search?: string
  jobType?: string
  sortBy?: 'created_at' | 'status' | 'job_type' | 'progress'
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface MarketDataJobsResponse {
  items: MarketDataJobResponse[]
  stats: {
    active: number
    completed: number
    /** EVERY failure, exhausted or not — what the "Fallidos" card shows. */
    failed: number
    cancelled: number
    /**
     * Failures the retry endpoint would actually update
     * (`current_retry < max_retries`) — what "Reintentar Todos Fallidos (N)"
     * may offer.
     *
     * Separate from `failed` because the button used to show the total and then
     * silently retry nothing: with every failure retry-exhausted it promised 25
     * and delivered a 200 with `retriedCount: 0` and no message at all.
     */
    retryableFailed: number
    /**
     * Failures declared permanent (`error_data.permanent`): no retry and no
     * fresh download will fix them, so they are not "exhausted" either.
     */
    permanentFailed: number
  }
  total: number
  page: number
  limit: number
}

export interface JobTimelineEvent {
  id: string
  eventType: string
  timestamp: string
  message?: string
  data?: Record<string, unknown>
}

// ============================================================
// ASSET DETAIL (expanded row in inventory)
// ============================================================

export interface AssetTimeframeEntry {
  interval: CandleInterval
  source: string
  recordCount: number
  firstDate: string
  lastDate: string
  coveragePercent: number
  gapCount: number
  totalMissingDays: number
  gaps: MarketDataGap[]
}

export interface AssetDetailResponse {
  assetId: string
  assetName: string
  assetSymbol: string
  assetType: string
  assetCategory: string | null
  coingeckoId: string | null
  sources: string[]
  totalRecords: number
  dateRangeStart: string | null
  dateRangeEnd: string | null
  timeframes: AssetTimeframeEntry[]
  missingTimeframes: CandleInterval[]
}

// ============================================================
// FX INVENTORY
// ============================================================

export interface FxInventoryStats {
  totalCurrencies: number
  totalPairs: number
  totalRecords: number
  dateRangeStart: string | null
  dateRangeEnd: string | null
  currenciesWithGaps: number
}

export interface FxCurrencyCoverage {
  currency: string
  pairCount: number
  totalRecords: number
  firstDate: string
  lastDate: string
  expectedDays: number
  coveragePercent: number
  gapCount: number
  totalMissingDays: number
}

export interface FxInventoryCoverageQuery {
  search?: string
  gapsOnly?: boolean
  page?: number
  limit?: number
}

// ============================================================
// FX CURRENCY DETAIL (expanded row)
// ============================================================

export interface FxPairDetail {
  baseCurrency: string
  quoteCurrency: string
  source: string
  recordCount: number
  firstDate: string
  lastDate: string
  coveragePercent: number
  gaps: MarketDataGap[]
}

export interface FxCurrencyDetailResponse {
  currency: string
  totalRecords: number
  pairCount: number
  sources: string[]
  dateRangeStart: string | null
  dateRangeEnd: string | null
  pairs: FxPairDetail[]
}

// ============================================================
// FX MATRIX
// ============================================================

export interface FxMatrixResponse {
  currencies: string[]
  matrix: Record<string, Record<string, number | null>>
  pairsWithGaps: Array<{ base: string; quote: string; gapCount: number; totalMissingDays: number }>
}

// ============================================================
// FX FILL GAPS
// ============================================================

export interface FillFxGapsRequest {
  currency: string
  gaps: MarketDataGap[]
}
